/**
 * Consent detail — full purpose, data categories, lawful basis,
 * grant/decline/revoke actions, and the read audit log.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import {
  ArrowLeft, Building2, CheckCircle2, XCircle, Ban, Clock, KeyRound,
  Eye, ShieldCheck, Loader2, AlertTriangle,
} from "lucide-react";
import {
  getConsentDetail, decideConsent, revokeConsent,
  PURPOSE_LABELS, CATEGORY_LABELS, LAWFUL_BASIS_LABELS,
} from "@/lib/consent";
import { emitSignal } from "@/lib/wallet-signal";
import { toast } from "sonner";

export default function WalletConsentDetail() {
  const { consentId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [revokeReason, setRevokeReason] = useState("");
  const [showRevoke, setShowRevoke] = useState(false);

  const load = async () => {
    try {
      const data = await getConsentDetail(consentId);
      setC(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Load failed");
    }
  };

  useEffect(() => {
    emitSignal("wallet.consent.detail_opened", { consent_id: consentId });
    load();
  }, [consentId]);

  const onDecide = async (decision) => {
    setBusy(true);
    try {
      await decideConsent(consentId, decision);
      emitSignal(`wallet.consent.${decision}ed`, { consent_id: consentId });
      toast.success(decision === "grant" ? "Consent granted" : "Consent declined");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async () => {
    setBusy(true);
    try {
      await revokeConsent(consentId, revokeReason.trim() || null);
      emitSignal("wallet.consent.revoked", { consent_id: consentId });
      toast.success("Consent revoked");
      setShowRevoke(false);
      setRevokeReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <WalletShell activeTab="settings">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div data-testid="consent-detail-error" className="rounded-2xl bg-rose-50 border border-rose-200 p-6 text-sm text-rose-800">
            {error}
          </div>
          <Link to="/wallet/consent" className="mt-4 inline-flex items-center gap-1 text-sm text-slate-600">
            <ArrowLeft className="w-4 h-4" /> Back to My Consent
          </Link>
        </div>
      </WalletShell>
    );
  }

  if (!c) {
    return (
      <WalletShell activeTab="settings">
        <div className="max-w-3xl mx-auto px-4 py-10 text-sm text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </WalletShell>
    );
  }

  const purposeLabel = PURPOSE_LABELS[c.purpose] || c.purpose;

  return (
    <WalletShell activeTab="settings">
      <div className="max-w-3xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-6" data-testid="consent-detail-page">
        <button onClick={() => navigate("/wallet/consent")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft className="w-4 h-4" /> Back to My Consent
        </button>

        {/* Header card */}
        <section className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6">
          <div className="flex items-start gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: `${GOLD}22`, color: NAVY }}
            >
              <KeyRound className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Consent request</div>
              <h1 className="font-display text-2xl font-semibold mt-1" style={{ color: NAVY }}>{purposeLabel}</h1>
              <div className="mt-2 flex items-center gap-1.5 text-[12px] text-slate-600">
                <Building2 className="w-3.5 h-3.5" />
                <span data-testid="consent-tenant-name">{c.tenant_name}</span>
              </div>
            </div>
            <StatusBadge status={c.status} />
          </div>
          <div className="mt-4 rounded-lg p-3 text-[13px] leading-relaxed" style={{ background: "#F8FAFC", color: NAVY }} data-testid="consent-purpose-desc">
            {c.purpose_description}
          </div>
          <div className="mt-3 text-[11px] text-slate-500">
            Lawful basis: <span className="font-semibold" style={{ color: NAVY }}>{LAWFUL_BASIS_LABELS[c.lawful_basis] || c.lawful_basis}</span>
          </div>
        </section>

        {/* Data categories */}
        <section className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6">
          <h2 className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: NAVY }}>What they want to see</h2>
          <ul className="mt-3 space-y-2" data-testid="consent-categories">
            {c.data_categories.map((cat) => (
              <li key={cat} className="flex items-start gap-2 text-[13px] text-slate-700">
                <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: GOLD }} />
                <span>{CATEGORY_LABELS[cat] || cat}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Actions */}
        {c.status === "pending" && (
          <section className="rounded-2xl border p-6" style={{ borderColor: `${GOLD}55`, background: `${GOLD}0A` }}>
            <h2 className="font-display text-lg font-semibold" style={{ color: NAVY }}>Your decision</h2>
            <p className="text-[12px] text-slate-600 mt-1">Once granted, this consent is valid until {new Date(c.expires_at).toLocaleDateString()}. You can revoke it at any time.</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                data-testid="consent-grant-btn"
                onClick={() => onDecide("grant")}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: "#065F46" }}
              >
                <CheckCircle2 className="w-4 h-4" /> Grant access
              </button>
              <button
                data-testid="consent-decline-btn"
                onClick={() => onDecide("decline")}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <XCircle className="w-4 h-4" /> Decline
              </button>
            </div>
          </section>
        )}

        {c.status === "granted" && (
          <section className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6">
            <h2 className="font-display text-lg font-semibold" style={{ color: NAVY }}>Active consent</h2>
            <p className="text-[12px] text-slate-600 mt-1">
              Granted on {c.decided_at ? new Date(c.decided_at).toLocaleDateString() : "—"}. Expires {new Date(c.expires_at).toLocaleDateString()}.
            </p>
            {!showRevoke ? (
              <button
                data-testid="consent-revoke-btn"
                onClick={() => setShowRevoke(true)}
                disabled={busy}
                className="mt-4 inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold border border-rose-200 text-rose-700 hover:bg-rose-50"
              >
                <Ban className="w-4 h-4" /> Revoke consent
              </button>
            ) : (
              <div className="mt-4 space-y-3">
                <div className="flex items-start gap-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Revoking stops future data reads immediately. Past reads remain in your access log.</span>
                </div>
                <input
                  type="text"
                  data-testid="consent-revoke-reason"
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  placeholder="Reason (optional)"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <div className="flex gap-2">
                  <button
                    data-testid="consent-revoke-confirm"
                    onClick={onRevoke}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white bg-rose-700 hover:bg-rose-800 disabled:opacity-50"
                  >
                    Confirm revoke
                  </button>
                  <button
                    onClick={() => { setShowRevoke(false); setRevokeReason(""); }}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-slate-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Access log */}
        <section className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6" data-testid="consent-access-log">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" style={{ color: GOLD }} />
            <h2 className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: NAVY }}>Who has seen this — {c.access_count || 0}</h2>
          </div>
          {(!c.access_log || c.access_log.length === 0) ? (
            <p className="text-[12px] text-slate-500 mt-3">
              {c.status === "granted" ? "The institution hasn't read your data yet." : "No reads recorded."}
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {c.access_log.slice().reverse().map((e, idx) => (
                <li key={idx} className="text-[12px] text-slate-700 border-l-2 pl-3 py-1" style={{ borderColor: GOLD }}>
                  <div className="font-semibold" style={{ color: NAVY }}>{e.actor}</div>
                  <div className="text-slate-500 text-[11px]">
                    {new Date(e.accessed_at).toLocaleString()}
                    {e.reason && <> · {e.reason}</>}
                  </div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    Read: {e.data_categories_read.map((c) => CATEGORY_LABELS[c] || c).join(", ")}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </WalletShell>
  );
}

function StatusBadge({ status }) {
  const tone = {
    pending:  { color: "#B45309", bg: "#FEF3C7", label: "Pending", Icon: Clock },
    granted:  { color: "#065F46", bg: "#D1FAE5", label: "Granted", Icon: CheckCircle2 },
    declined: { color: "#7F1D1D", bg: "#FEE2E2", label: "Declined", Icon: XCircle },
    revoked:  { color: "#475569", bg: "#E2E8F0", label: "Revoked", Icon: Ban },
    expired:  { color: "#475569", bg: "#E2E8F0", label: "Expired", Icon: Ban },
  }[status] || { color: "#475569", bg: "#E2E8F0", label: status, Icon: Clock };
  const Icon = tone.Icon;
  return (
    <span
      data-testid={`consent-status-badge-${status}`}
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded font-semibold"
      style={{ background: tone.bg, color: tone.color }}
    >
      <Icon className="w-3 h-3" /> {tone.label}
    </span>
  );
}
